/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import clsx from 'clsx'
import UiPill from 'components/Ui/UiPill'
import {ReservationOfferStatus} from 'types/enums/ReservationOfferStatus'
import {ReservationStatus} from 'types/enums/ReservationStatus'
import {useTranslation} from 'react-i18next'

interface UiReservationStatusPillProps {
    status: ReservationOfferStatus | ReservationStatus | string
    children?: React.ReactNode
    className?: string
}

const UiReservationStatusPill: React.FC<UiReservationStatusPillProps> = ({
    status,
    children,
    className,
}) => {
    const { t } = useTranslation()

    const getStatusColors = () => {
        switch (status) {
            case ReservationOfferStatus.Lead:
            case ReservationStatus.Creating:
                return 'pill-tone-blue'
            case ReservationOfferStatus.New:
            case ReservationOfferStatus.Accepted:
            case ReservationStatus.Confirmed:
                return 'pill-tone-green'
            case ReservationOfferStatus.Sent:
            case ReservationStatus.Reserved:
                return 'pill-tone-indigo'
            case ReservationOfferStatus.Finished:
        case ReservationStatus.Finished:
            return 'pill-tone-blue'
        case ReservationStatus.Deleted:
            return 'pill-tone-slate'
            case ReservationOfferStatus.Declined:
            case ReservationStatus.Cancelled:
                return 'pill-tone-red'
            case ReservationOfferStatus.Reconfirm:
            case ReservationStatus.Pending:
                return 'pill-tone-yellow'
            case ReservationStatus.Checked:
                return 'pill-tone-emerald'
            case ReservationStatus.NoShow:
                return 'pill-tone-orange'
            case ReservationStatus.Rebooking:
                return 'pill-tone-purple'
            case ReservationStatus.GroupPackage:
                return 'pill-tone-cyan'
            case ReservationOfferStatus.All:
            case ReservationStatus.All:
            default:
                return 'pill-tone-gray'
        }
    }

    const normalizedLabel = String(status || '')
        .replaceAll('_', ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())

    const label = t(status === ReservationStatus.Finished ? 'Completed' : normalizedLabel)

    return (
        <UiPill className={clsx('UiReservationStatusPill', getStatusColors(), className)}>
            {children || label}
        </UiPill>
    )
}

export default UiReservationStatusPill
